import { Button, Col, Flex, Form, Modal, Row } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// components
import FormElement from 'components/formElement/formElement';
import PhoneInputComponent, { PhoneInputReturn } from 'components/phoneInput/phoneInput';
import Icon from 'utils/icon';

// services
import { checkPhoneValidation } from 'utils/validators/phone';
import { onCheckNationalId, onSaveShareholder, onUpdateShareholder } from '../services/shareholdersService';

// models
import { SaveShareholderPayload, ShareholderFromNationalId } from '../models/shareholder';
import NotificationService from 'core/services/notification';
import SelectElement from 'components/selectElement/selectElement';
import { formatErrorMessages } from 'utils/errorMessage';
import { FormProps } from 'antd/es/form/Form';
import { Shareholder } from 'core/models/shareholder';
import { mapResponseErrors } from 'utils/mapResponseErrors';
import FormNumberElement from 'components/formNumberElement/formNumberElement';

interface ShareholderModalProps {
	remainingShares: number;
	open: boolean;
	onCancel: () => void;
	onConfirm: () => void;
	shareholderData?: Shareholder; // is exist so we are in edit mode
}

const ShareholderModal: FunctionComponent<ShareholderModalProps> = ({ open, onCancel, onConfirm, shareholderData, remainingShares }) => {
	const { t } = useTranslation();

	const [shareholderForm] = Form.useForm();

	const [shareholder, setShareholder] = useState<ShareholderFromNationalId>();
	const [isLoading, setIsLoading] = useState(false);

	const initialFormValues = {
		name: '',
		national_id: '',
		phone_number: '',
		phone_country_code: '',
		founder: 'No',
		executive: 'No',
		free_common_shares_count: 0,
		fully_paid: true,
	};

	const truthySelectOptions = [
		{ value: false, label: t('COMMON.NO') },
		{ value: true, label: t('COMMON.YES') },
	];

	const yesNoSelectOptions = [
		{ value: 'No', label: t('COMMON.NO') },
		{ value: 'Yes', label: t('COMMON.YES') },
	];

	type shareholderFieldTypes = {
		national_id: string;
		alias: string;
		phone_number: string;
		phone_country_code: string;
		founder: string;
		executive: string;
		free_common_shares_count: number;
		fully_paid: boolean;
	};

	useEffect(() => {
		// check if shareholderData is provided, if so, we are in edit mode
		if (shareholderData) {
			shareholderForm.setFieldsValue({
				...shareholderData,
				phone_number: shareholderData.phone_number,
				phone_country_code: shareholderData.phone_country_code,
				total_common_shares_count:
					shareholderData.free_common_shares_count + shareholderData.mortgaged_common_shares_count + shareholderData.suspended_common_shares_count,
			});
			setShareholder({
				alias: shareholderData.alias,
				phone_number: shareholderData.phone_number,
				phone_country_code: shareholderData.phone_country_code,
			} as ShareholderFromNationalId);
		}
	}, [shareholderData]);

	const onChangePhone = ({ number, dialCode }: PhoneInputReturn) => {
		shareholderForm.setFieldsValue({
			phone_number: number,
			phone_country_code: dialCode,
		});
	};

	const validatePhone = (_: any, value: any) => {
		if (checkPhoneValidation(shareholderForm.getFieldValue('phone_country_code'), value)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.PHONE_INVALID')));
		else return Promise.reject();
	};

	const checkNationalIdHandler = async (value: string) => {
		if (value.length === 10) {
			try {
				const response = await onCheckNationalId(value);

				if (response.success) {
					setShareholder(response.data);

					shareholderForm.setFieldsValue({
						alias: response.data.alias,
						phone_number: response.data.phone_number,
						phone_country_code: response.data.phone_country_code,
					});
				}
			} catch (error: any) {
				if (error.response.status === 422) {
					mapResponseErrors(error, shareholderForm);
					return;
				}
				if (error.response.status === 404) return;
				else NotificationService.error(t('COMMON.ERROR'), formatErrorMessages(error));
			}
		} else {
			setShareholder(undefined);

			shareholderForm.setFieldsValue({
				alias: '',
				phone_number: '',
				phone_country_code: '',
			});
		}
	};

	const saveShareholderHandler: FormProps<shareholderFieldTypes>['onFinish'] = async (values) => {
		try {
			setIsLoading(true);

			const payload: SaveShareholderPayload = {
				alias: shareholderForm.getFieldValue('alias'),
				national_id: shareholderForm.getFieldValue('national_id'),
				phone_number: shareholderForm.getFieldValue('phone_number'),
				phone_country_code: shareholderForm.getFieldValue('phone_country_code'),
				founder: shareholderForm.getFieldValue('founder'),
				executive: shareholderForm.getFieldValue('executive'),
				free_common_shares_count: shareholderForm.getFieldValue('free_common_shares_count'),
				fully_paid: shareholderForm.getFieldValue('fully_paid') ? '1' : '0',
			};

			if (shareholderData) await onUpdateShareholder(shareholderData.id, payload);
			else await onSaveShareholder(payload);

			setIsLoading(false);
			NotificationService.success(
				t('COMMON.SUCCESS'),
				shareholderData ? t('SHAREHOLDERS_REGISTRY.SHAREHOLDER_UPDATED') : t('SHAREHOLDERS_REGISTRY.SHAREHOLDER_ADDED'),
			);
			onConfirm();
			setTimeout(() => {
				shareholderForm.resetFields();
				setShareholder(undefined);
			}, 500);
		} catch (error: any) {
			setIsLoading(false);

			if (error.status === 422) {
				mapResponseErrors(error, shareholderForm);
			} else NotificationService.error(t('COMMON.ERROR'), formatErrorMessages(error));
		}
	};

	const cancelShareholderHandler = () => {
		onCancel();
		shareholderForm.resetFields();
		setShareholder(undefined);
	};

	return (
		<Modal
			open={open}
			style={{
				top: 0,
				marginTop: 20,
				maxHeight: 'calc(100vh - 45px)',
				overflow: 'auto',
				paddingBottom: 0,
				borderRadius: 'var(--radius)',
			}}
			title={
				<Flex gap={10}>
					<Icon name="info" width={40} height={40} />
					<div>
						<h6 className="mb-0">{t('SHAREHOLDERS_REGISTRY.ADD_NEW_SHAREHOLDER')}</h6>
						<span className="text-light" style={{ fontSize: 14 }}>
							{t('SHAREHOLDERS_REGISTRY.ADD_ALL_DATA_MANUALLY')}
						</span>
					</div>
				</Flex>
			}
			footer={[
				<Button key="discard" type="link" danger onClick={cancelShareholderHandler}>
					{t('COMMON.CANCEL')}
				</Button>,
				<Button key="submit" type="primary" onClick={shareholderForm.submit} loading={isLoading} disabled={isLoading}>
					{shareholderData ? t('COMMON.UPDATE') : t('COMMON.ADD')}
				</Button>,
			]}
			onCancel={cancelShareholderHandler}>
			<Form form={shareholderForm} initialValues={initialFormValues} onFinish={saveShareholderHandler}>
				<FormElement
					name="national_id"
					label={t('COMMON.NATIONAL_ID')}
					fullWidth
					type="number"
					onChange={checkNationalIdHandler}
					rules={[
						{ required: true, message: t('COMMON.FIELD_REQUIRED') },
						{ len: 10, message: t('COMMON.NATIONAL_ID_LENGTH') },
					]}
				/>

				<FormElement
					name="alias"
					label={t('COMMON.NAME')}
					fullWidth
					type="text"
					disabled={!!shareholder?.alias}
					rules={[
						{ required: true, message: t('COMMON.FIELD_REQUIRED') },
						{ min: 12, message: t('COMMON.FIELD_LENGTH_VALIDATION') },
					]}
				/>

				<Form.Item
					name="phone_number"
					style={{ maxWidth: 'unset' }}
					rules={[{ required: true, message: t('COMMON.PHONE_REQUIRED') }, { validator: validatePhone }]}>
					<PhoneInputComponent
						fullWidth
						onChange={onChangePhone}
						userPhoneNumber={shareholder?.phone_number}
						userDialCode={shareholder?.phone_country_code}
						disabled={!!shareholder?.phone_number && !shareholderData}
					/>
				</Form.Item>

				<Form.Item name="phone_country_code" hidden />

				<Row gutter={20}>
					<Col span={24} md={12}>
						<SelectElement name="founder" label={t('COMMON.FOUNDER')} options={yesNoSelectOptions} />
					</Col>
					<Col span={24} md={12}>
						<SelectElement name="executive" label={t('COMMON.EXECUTIVE')} options={yesNoSelectOptions} />
					</Col>
				</Row>

				<div className="my-3">
					<Row className="mb-2" gutter={20}>
						<Col span={24} md={12}>
							<FormNumberElement
								label={t('COMMON.TOTAL_COMMON_SHARES')}
								name="free_common_shares_count"
								rules={[
									{
										type: 'number',
										max: remainingShares,
										message: t('SHAREHOLDERS_REGISTRY.EXCEEDS_REMAINING_SHARES', { num: remainingShares.toLocaleString() }),
									},
									{ type: 'number', min: 1, message: t('COMMON.FIELD_REQUIRED') },
								]}
							/>
						</Col>
						<Col span={24} md={12}>
							<SelectElement name="fully_paid" label={t('COMMON.FULLY_PAID')} options={truthySelectOptions} />
						</Col>
					</Row>
				</div>
			</Form>
		</Modal>
	);
};

export default ShareholderModal;
